import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAiModels, updateAiModel } from '../../Api/AiModels';

const TOKEN_PARAM_LABEL = {
  max_tokens: 'max_tokens',
  max_completion_tokens: 'max_completion_tokens',
};

const API_LABEL = {
  chat_completions: 'Chat Completions',
  anthropic_messages: 'Anthropic Messages',
  responses: 'Chat Completions · tools via Responses',
};

/** GPT reasoning models answer over Chat Completions but run tool calls over the
 * Responses API (`tool_api`), because Foundry refuses tools with reasoning on Chat Completions. */
const apiLabel = (capability) =>
  capability?.tool_api === 'responses'
    ? API_LABEL.responses
    : API_LABEL[capability?.api] || capability?.api;

/** "Yes · default low" for a reasoning model. `supports_reasoning` is missing when the
 * backend predates the field, so it reads as unknown, not as no. */
const reasoningLabel = (capability) => {
  if (capability?.supports_reasoning == null) return 'Unknown';
  if (!capability.supports_reasoning) return 'No';
  return capability.reasoning_default ? `Yes · default ${capability.reasoning_default}` : 'Yes';
};

/** The reasoning level a request sends, mirroring azure_chat._resolve_slot: the slot's
 * own pick, then its default in code, then the model's default. Null when the model has
 * no reasoning or none of those levels applies to it. */
export const effectiveEffort = (slot, capability, effort) => {
  const allowed = capability?.reasoning_efforts || [];
  if (!allowed.length) return null;
  return [effort, slot?.default_reasoning_effort, capability.reasoning_default].find((v) => v && allowed.includes(v)) || null;
};

const PROVIDER_LABEL = {
  foundry: 'Azure AI Foundry',
  anthropic: 'Claude via Foundry',
  vertex: 'Google Vertex AI',
};

const RESIDENCY_LABEL = {
  australia: 'Australia',
  singapore: 'Singapore',
};

/** Provider + region chips for one deployment. The in-region flag comes from the
 * backend (AI_RESIDENCY_REGION, set per environment in Helm), never from the
 * browser URL. null = this environment makes no residency claim. */
const ResidencyChips = ({ capability }) => {
  if (!capability) return null;
  const provider = PROVIDER_LABEL[capability.provider] || capability.provider;
  const color = capability.in_region === true ? 'success' : capability.in_region === false ? 'warning' : 'default';
  const title =
    capability.in_region === true
      ? 'Prompts are processed in this environment\'s data-residency region'
      : capability.in_region === false
        ? 'Prompts may be processed outside this environment\'s data-residency region'
        : 'No data-residency region is configured for this environment';
  return (
    <>
      <Chip size="small" variant="outlined" label={provider} sx={{ mr: 0.5 }} />
      {capability.region ? (
        <Chip size="small" color={color} variant="outlined" label={capability.region} title={title} />
      ) : null}
    </>
  );
};

const SYSTEM_ROLE_LABEL = {
  system: 'system',
  developer: 'developer',
  merge_into_user: 'merged into first user turn',
};

/** Zavvy's tool loop runs on GPT and Gemini deployments. `supports_tools` is missing
 * when the backend predates the field, so it reads as unknown, not as no. */
const toolsLabel = (capability) => {
  if (capability?.supports_tools == null) return 'Unknown';
  return capability.supports_tools ? 'Yes' : 'No';
};

/** A slot that runs the tool loop cannot use a deployment without tool calling;
 * the backend refuses it too. */
export const blockedForSlot = (slot, capability) =>
  Boolean(slot?.needs_tools) && capability?.supports_tools === false;

/** One-line, read-only summary of what a deployment's request actually looks
 * like — sourced from the backend's `capabilities` map so it can never drift
 * from azure_chat.py's real request-building logic. */
const CapabilitySummary = ({ capability, effort }) => {
  if (!capability) return null;
  const tokenParam = TOKEN_PARAM_LABEL[capability.token_param] || capability.token_param;
  const reasoning = capability.supports_reasoning
    ? `${effort || capability.reasoning_default || 'yes'}${capability.reasoning_summaries ? ' (summaries)' : ''}`
    : reasoningLabel(capability).toLowerCase();
  return (
    <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mt: 0.5 }}>
      Token cap: {capability.token_cap} ({tokenParam}) · Temperature:{' '}
      {capability.supports_temperature ? 'supported' : 'not supported'} · JSON mode:{' '}
      {capability.supports_json_mode ? 'native' : 'prompt-only'} · Vision:{' '}
      {capability.supports_vision ? 'yes' : 'no'} · Tools: {toolsLabel(capability).toLowerCase()} · Reasoning:{' '}
      {reasoning} · API: {apiLabel(capability)}
    </Typography>
  );
};

/** Compact reference table across every allowed deployment, so an admin
 * comparing models doesn't have to click into each slot to see the shape. */
const CapabilityReferenceTable = ({ deployments, capabilities }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 880 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
      Deployment capabilities
    </Typography>
    <Typography variant="body2" sx={{ color: '#6B7280', mb: 1.5 }}>
      Read only — derived from the deployment name, the same way the backend builds each
      request. Not editable here; assign a deployment to a slot below.
    </Typography>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Deployment</TableCell>
            <TableCell>Provider / region</TableCell>
            <TableCell>API</TableCell>
            <TableCell>Token param</TableCell>
            <TableCell align="right">Token cap</TableCell>
            <TableCell>Temperature</TableCell>
            <TableCell>Reasoning</TableCell>
            <TableCell>JSON mode</TableCell>
            <TableCell>Vision</TableCell>
            <TableCell>Tools</TableCell>
            <TableCell>System role</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deployments.map((name) => {
            const cap = capabilities?.[name];
            if (!cap) return null;
            return (
              <TableRow key={name}>
                <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                <TableCell>
                  <ResidencyChips capability={cap} />
                </TableCell>
                <TableCell>{apiLabel(cap)}</TableCell>
                <TableCell>{TOKEN_PARAM_LABEL[cap.token_param] || cap.token_param}</TableCell>
                <TableCell align="right">{cap.token_cap}</TableCell>
                <TableCell>{cap.supports_temperature ? 'Yes' : 'No'}</TableCell>
                <TableCell>{reasoningLabel(cap)}</TableCell>
                <TableCell>{cap.supports_json_mode ? 'Native' : 'Prompt-only'}</TableCell>
                <TableCell>{cap.supports_vision ? 'Yes' : 'No'}</TableCell>
                <TableCell>{toolsLabel(cap)}</TableCell>
                <TableCell>{SYSTEM_ROLE_LABEL[cap.system_role] || cap.system_role}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const TrinityAiModels = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  // Reasoning level per slot; '' is the slot's default in code.
  const [effortDraft, setEffortDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAiModels();
      setData(res?.data || null);
      const next = {};
      const efforts = {};
      (res?.data?.slots || []).forEach((slot) => {
        next[slot.id] = slot.deployment;
        efforts[slot.id] = slot.reasoning_effort || '';
      });
      setDraft(next);
      setEffortDraft(efforts);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err?.message || 'Failed to load AI models');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    (data?.slots || []).forEach((slot) => {
      const list = map.get(slot.group) || [];
      list.push(slot);
      map.set(slot.group, list);
    });
    return Array.from(map.entries());
  }, [data]);

  /** Saves what changed on one slot: only the keys sent are updated. `reset` clears both. */
  const save = async (slot, { deployment, effort, reset } = {}) => {
    const body = { slot_id: slot.id };
    if (reset) Object.assign(body, { deployment: null, reasoning_effort: null });
    else {
      if (deployment !== slot.deployment) body.deployment = deployment;
      if (effort !== (slot.reasoning_effort || '')) body.reasoning_effort = effort || null;
    }
    setSaving(slot.id);
    setError('');
    setNotice('');
    try {
      const res = await updateAiModel(body);
      const updated = res?.data?.slot;
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          slots: prev.slots.map((s) => (s.id === slot.id ? updated : s)),
        };
      });
      setDraft((prev) => ({ ...prev, [slot.id]: updated.deployment }));
      setEffortDraft((prev) => ({ ...prev, [slot.id]: updated.reasoning_effort || '' }));
      const level = updated.reasoning_effort || `the default (${updated.default_reasoning_effort || 'off'})`;
      setNotice(
        reset
          ? `${slot.label} reset to the env default (${updated.deployment}).`
          : 'deployment' in body
            ? `${slot.label} now uses ${updated.deployment}. New requests pick this up within a few seconds — no restart.`
            : `${slot.label} now uses reasoning level ${level}. New requests pick this up within a few seconds.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err?.message || 'Failed to save model');
    } finally {
      setSaving('');
    }
  };

  /** Who sees Zavvy's reasoning text, models, tokens and the agent map. The backend
   * enforces it: with "admins", other roles never receive that data. */
  const saveVisibility = async (value) => {
    if (!value || value === data?.zavvy_reasoning_visibility) return;
    setSaving('visibility');
    setError('');
    setNotice('');
    try {
      const res = await updateAiModel({ zavvy_reasoning_visibility: value });
      const saved = res?.data?.zavvy_reasoning_visibility || value;
      setData((prev) => (prev ? { ...prev, zavvy_reasoning_visibility: saved } : prev));
      setNotice(
        saved === 'admins'
          ? 'Zavvy reasoning and the agent map now show to Super Admin and Tech Admin only. Other roles see the step list without reasoning, models or tokens.'
          : 'Zavvy reasoning and the agent map now show to everyone.'
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err?.message || 'Failed to save the setting');
    } finally {
      setSaving('');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          Loading model catalog…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        AI Models
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', mb: 2, maxWidth: 820 }}>
        Choose the deployment (Azure AI Foundry, Claude on Foundry, or Google Vertex AI) for
        each feature, and read the system prompt the app actually sends. Slots marked
        "Calls tools" need a GPT or Gemini deployment; GPT reasoning models run tools over
        the Responses API. Claude runs over the Anthropic Messages API and cannot be used
        there. Reasoning sets how hard a reasoning model thinks for that slot; Default uses
        the level set in code.
        Instructions live in code, not on the provider's deployment card. Changing a slot
        here does not move the others. Only models allowed for this environment are listed.
        Embeddings stay read-only. Zavvy slots (zavvy_*) are in this same list, with the
        prompt the runtime sends.
      </Typography>

      {data?.zavvy_reasoning_visibility ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2, maxWidth: 880 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} id="zavvy-reasoning-visibility">
            Zavvy reasoning and agent map
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-labelledby="zavvy-reasoning-visibility"
            value={data.zavvy_reasoning_visibility}
            disabled={saving === 'visibility'}
            onChange={(e, value) => saveVisibility(value)}
          >
            <ToggleButton value="everyone" sx={{ textTransform: 'none', px: 1.5 }}>
              Everyone
            </ToggleButton>
            <ToggleButton value="admins" sx={{ textTransform: 'none', px: 1.5 }}>
              Super Admin and Tech Admin only
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            Other roles still see which agents ran, without reasoning, models or tokens.
          </Typography>
        </Box>
      ) : null}

      {data?.environment || data?.residency_region ? (
        <Alert severity="info" sx={{ mb: 2, maxWidth: 880 }}>
          Environment <strong>{data.environment || 'unnamed'}</strong>
          {data.residency_region
            ? ` · data residency: ${RESIDENCY_LABEL[data.residency_region] || data.residency_region}`
            : ''}
          . Region chips turn orange for a model whose prompts may be processed outside that
          region.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      ) : null}

      {data?.embedding ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 880 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {data.embedding.label}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
            {data.embedding.description}
          </Typography>
          <Chip size="small" label={data.embedding.deployment} sx={{ mr: 1 }} />
          <ResidencyChips capability={data.embedding} />
          <Chip size="small" label="Read only" variant="outlined" sx={{ ml: 0.5 }} />
          {data.embedding.provider === 'vertex' && data.embedding.in_region == null ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#B45309' }}>
              Google does not document an in-region processing guarantee for embeddings; the
              request goes to the regional endpoint only.
            </Typography>
          ) : null}
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#6B7280' }}>
            {data.embedding.prompt_note}
          </Typography>
        </Paper>
      ) : null}

      {data?.allowed_deployments?.length ? (
        <CapabilityReferenceTable
          deployments={data.allowed_deployments}
          capabilities={data.capabilities}
        />
      ) : null}

      {groups.map(([group, slots]) => (
        <Box key={group} sx={{ mb: 3, maxWidth: 880 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {group}
          </Typography>
          {slots.map((slot) => {
            const selected = draft[slot.id] || slot.deployment;
            const effort = effortDraft[slot.id] ?? (slot.reasoning_effort || '');
            const dirty = selected !== slot.deployment || effort !== (slot.reasoning_effort || '');
            // A saved override the backend cannot use (slot.warning) is not what runs:
            // describe the default instead until the admin picks something else. A
            // deployment that can call tools is what runs, whatever else the warning says
            // (a reasoning level ignored on gpt-4.1, for instance).
            const ignored =
              Boolean(slot.warning) && slot.overridden && data?.capabilities?.[slot.deployment]?.supports_tools !== true;
            const shown = selected === slot.deployment && ignored ? slot.default_deployment : selected;
            const shownCap = data?.capabilities?.[shown];
            const efforts = shownCap?.reasoning_efforts || [];
            // The saved level stays on the slot when it moves to a model without that
            // level; the backend keeps it and uses it again on a model that has it.
            const effortIgnored = Boolean(effort) && !efforts.includes(effort);
            const defaultBlocked = blockedForSlot(slot, data?.capabilities?.[slot.default_deployment]);
            return (
              <Paper key={slot.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {slot.label}
                  </Typography>
                  {slot.overridden && ignored ? (
                    <Chip size="small" color="warning" label="Override not used" />
                  ) : slot.overridden ? (
                    <Chip size="small" color="primary" label="Override" />
                  ) : (
                    <Chip size="small" variant="outlined" label="Env default" />
                  )}
                  {!slot.implemented ? (
                    <Chip size="small" color="warning" variant="outlined" label="Not wired yet" />
                  ) : null}
                  {slot.needs_tools ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Calls tools"
                      title="Runs Zavvy's tool loop: needs a GPT or Gemini deployment"
                    />
                  ) : null}
                </Box>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 1.5 }}>
                  {slot.description}
                </Typography>
                {slot.warning ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {slot.warning}
                  </Alert>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id={`${slot.id}-model`}>Deployment</InputLabel>
                    <Select
                      labelId={`${slot.id}-model`}
                      label="Deployment"
                      value={selected}
                      onChange={(e) => {
                        const next = e.target.value;
                        setDraft((prev) => ({ ...prev, [slot.id]: next }));
                        // A level picked here that the new model lacks would be refused (400),
                        // so it falls back to the saved one, which is only kept, not sent.
                        const allowed = data?.capabilities?.[next]?.reasoning_efforts || [];
                        if (effort && !allowed.includes(effort)) {
                          setEffortDraft((prev) => ({ ...prev, [slot.id]: slot.reasoning_effort || '' }));
                        }
                      }}
                    >
                      {(data?.allowed_deployments || []).map((name) => {
                        const cap = data?.capabilities?.[name];
                        const blocked = blockedForSlot(slot, cap);
                        // The saved value stays selectable so a mistaken pick can be undone.
                        return (
                          <MenuItem
                            key={name}
                            value={name}
                            disabled={blocked && name !== slot.deployment}
                          >
                            {name}
                            {cap?.region
                              ? ` · ${cap.region}${cap.in_region === false ? ' (outside region)' : ''}`
                              : ''}
                            {blocked ? ' (no tool calling)' : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 170 }} disabled={!efforts.length}>
                    <InputLabel id={`${slot.id}-reasoning`} shrink>
                      Reasoning
                    </InputLabel>
                    <Select
                      labelId={`${slot.id}-reasoning`}
                      label="Reasoning"
                      notched
                      displayEmpty
                      value={efforts.includes(effort) ? effort : ''}
                      renderValue={(v) =>
                        !efforts.length ? 'Not a reasoning model' : v || `Default (${slot.default_reasoning_effort || 'off'})`
                      }
                      onChange={(e) => setEffortDraft((prev) => ({ ...prev, [slot.id]: e.target.value }))}
                    >
                      <MenuItem value="">Default ({slot.default_reasoning_effort || 'off'})</MenuItem>
                      {efforts.map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!dirty || saving === slot.id}
                    onClick={() => save(slot, { deployment: selected, effort })}
                  >
                    {saving === slot.id ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    size="small"
                    disabled={!slot.overridden || defaultBlocked || saving === slot.id}
                    title={
                      defaultBlocked
                        ? `${slot.default_deployment} cannot call tools, so resetting would break this slot`
                        : undefined
                    }
                    onClick={() => save(slot, { reset: true })}
                  >
                    Reset to default
                  </Button>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    Default: {slot.default_deployment}
                    {slot.updated_by ? ` · last set by ${slot.updated_by}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <ResidencyChips capability={shownCap} />
                </Box>
                {shownCap?.in_region === false ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {shown} is served from {shownCap.region}, outside this
                    environment's data-residency region
                    {data.residency_region
                      ? ` (${RESIDENCY_LABEL[data.residency_region] || data.residency_region})`
                      : ''}
                    . Prompts for this feature may leave the region.
                  </Alert>
                ) : null}
                {effortIgnored ? (
                  <Typography variant="caption" sx={{ display: 'block', color: '#B45309', mt: 1 }}>
                    The saved reasoning level ({effort}) is not used on {shown}. It applies again on
                    a model that supports it.
                  </Typography>
                ) : null}
                <CapabilitySummary capability={shownCap} effort={effectiveEffort(slot, shownCap, effort)} />
                {slot.id === 'extract_fields' && shownCap && !shownCap.supports_vision ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    This deployment isn't vision-capable — image-based field extraction will
                    fail; text documents are unaffected.
                  </Alert>
                ) : null}
                <Accordion disableGutters elevation={0} sx={{ mt: 1.5, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      System prompt
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {slot.prompt_note ? (
                      <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mb: 1 }}>
                        {slot.prompt_note}
                      </Typography>
                    ) : null}
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                        whiteSpace: 'pre-wrap',
                        fontSize: 12,
                        lineHeight: 1.5,
                        maxHeight: 320,
                        overflow: 'auto',
                      }}
                    >
                      {slot.system_prompt}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default TrinityAiModels;
